import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon
} from '@mui/icons-material';
import { getPeriodLabel, isCurrentPeriod, shiftAnchor } from '../utils/periodRange';

/**
 * Reusable Week/Month period filter with prev/next navigation and a "Today"
 * reset. Stateless — the parent owns `periodType` and `anchor`.
 *
 * @param {'list'|'week'|'month'} periodType  'list' only when showList is set
 * @param {boolean} [showList]  offer a "List" choice (all jobs, no date range)
 * @param {Date}   anchor       any date inside the selected period
 * @param {Date}   start        period start (from getPeriodRange)
 * @param {Date}   end          period end (from getPeriodRange)
 * @param {Function} onPeriodTypeChange  (newType) => void
 * @param {Function} onAnchorChange      (newAnchor: Date) => void
 */
const PeriodFilter = ({
  periodType,
  anchor,
  start,
  end,
  onPeriodTypeChange,
  onAnchorChange,
  showList = false
}) => {
  const isList = periodType === 'list';
  const label = isList ? '' : getPeriodLabel(periodType, start, end, anchor);
  const current = isList ? false : isCurrentPeriod(periodType, start, anchor);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
      mb={2}
    >
      <ToggleButtonGroup
        value={periodType}
        exclusive
        onChange={(_, value) => value && onPeriodTypeChange(value)}
        size="small"
      >
        {showList && <ToggleButton value="list">List</ToggleButton>}
        <ToggleButton value="week">Week</ToggleButton>
        <ToggleButton value="month">Month</ToggleButton>
      </ToggleButtonGroup>

      {!isList && (
        <>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={() => onAnchorChange(shiftAnchor(anchor, periodType, -1))}
              size="small"
              aria-label="previous period"
            >
              <PrevIcon />
            </IconButton>
            <Box textAlign="center" sx={{ minWidth: 150 }}>
              <Typography variant="body1" fontWeight={600}>
                {label}
              </Typography>
              {current && (
                <Typography variant="caption" color="text.secondary">
                  {periodType === 'week' ? 'This week' : 'This month'}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => onAnchorChange(shiftAnchor(anchor, periodType, 1))}
              size="small"
              aria-label="next period"
            >
              <NextIcon />
            </IconButton>
          </Box>

          <Button size="small" onClick={() => onAnchorChange(new Date())} disabled={current}>
            Today
          </Button>
        </>
      )}
    </Box>
  );
};

export default PeriodFilter;
